import { createPortal } from "react-dom";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { FullscreenModal, Button, CalendarInput, DropFileInput } from "shared";
import {
  useCloseTreasuryBonds,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import "./CloseTreasuryBondsModal.scss";

interface CloseTreasuryBondsModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
}

interface FormValues {
  closedAt: string;
  contractFile: File[] | null;
}

const validationSchema = Yup.object({
  closedAt: Yup.string().required("Data zamknięcia jest wymagana"),
  contractFile: Yup.mixed().nullable(),
});

const initialValues: FormValues = {
  closedAt: "",
  contractFile: null,
};

const CloseTreasuryBondsModal = ({
  isOpen,
  onClose,
  item,
}: CloseTreasuryBondsModalProps) => {
  const { mutateAsync: closeBonds, isPending } = useCloseTreasuryBonds();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { data: me } = useGetMe();

  const handleSubmit = async (values: FormValues) => {
    if (!item?.uuid) return;

    try {
      // Zamknij obligacje
      await closeBonds(
        {
          uuid: item.uuid,
          closedAt: values.closedAt,
        },
        {
          onSuccess: async (data) => {
            // Upload plików umów, jeśli zostały dodane
            if (
              Array.isArray(values.contractFile) &&
              values.contractFile.length > 0
            ) {
              try {
                const userId: string | number =
                  (me as any)?.user?.id ?? (me as any)?.id ?? "";

                const contractFiles = values.contractFile.filter(
                  (f) => f instanceof File,
                );

                await Promise.allSettled(
                  contractFiles.slice(0, 10).map(async (contract: File) => {
                    try {
                      const uploaded = await uploadFile({
                        file: contract,
                        type: "attachment",
                        parentId: userId,
                      });

                      const fileUuid = Array.isArray(uploaded)
                        ? uploaded[0]?.uuid
                        : (uploaded as any)?.uuid;

                      if (fileUuid && item.uuid) {
                        await attachFile({
                          investmentUuid: item.uuid,
                          fileUuid,
                          role: "closure_contract",
                        });
                      }
                    } catch (error) {
                      console.error("Error uploading closure contract:", error);
                    }
                  }),
                );
              } catch (error) {
                console.error("Error handling closure contracts:", error);
                // Pliki nie blokują zamknięcia obligacji
              }
            }

            onClose();
          },
        },
      );
    } catch (error) {
      // Błąd jest obsługiwany przez hook
    }
  };

  if (!item) return null;

  return createPortal(
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="close-treasury-bonds-modal">
        <h2 className="fullscreen-modal__header">Zamknij obligacje skarbowe</h2>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form className="close-treasury-bonds-modal__form">
              <div className="close-treasury-bonds-modal__fields">
                <div className="close-treasury-bonds-modal__field">
                  <Field
                    type="date"
                    name="closedAt"
                    label="Data zamknięcia"
                    component={CalendarInput}
                    errors={formik.errors}
                  />
                </div>
                <div className="close-treasury-bonds-modal__field">
                  <Field
                    label="Umowy (opcjonalnie)"
                    name="contractFile"
                    id="contractFile"
                    as={DropFileInput}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    optional
                    multiple={10}
                  />
                </div>
              </div>
              <div className="close-treasury-bonds-modal__actions">
                <Button
                  type="button"
                  onClick={onClose}
                  label="Anuluj"
                  className="button--stroke button--rounded"
                />
                <Button
                  type="submit"
                  onClick={() => {}}
                  label={isPending ? "Zapisywanie..." : "Potwierdź"}
                  className="button--rounded button--black"
                  isLoading={isPending}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullscreenModal>,
    document.body,
  );
};

export default CloseTreasuryBondsModal;
